import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { initializeWebSocket } from "../components/WebSocketService";

const GlobalStateContext = createContext();

export const GlobalStateProvider = ({ children }) => {
  const socketRef = useRef(null);

  const defaultState = {
    user: null,
    likes: [],
    isAuthenticated: false,
    conversations: [],
    showLogin: true,
    showLogins: false,
    showSignUp: false,
    showVerify: false,
    viewAsPublic: false,
    showUserInfo: true,
    goTo: null,
    showExplore: false,
    showNotifications: false,
    moreInfoPost: null,
    hasPublicPosts: null,
    messageJD: null,
    notificationCount: 0,
    lastUniqueMessagesCount: 0,
    refreshKeys: {},
    pageName: "posts",
    refreshKey: 0,
    selectedConvo: null,
    sessionExpired: false,
    anotherSignInModal: false,
    showLink: null,
    justPosted: null,
    duplicatePost: null,
    fullScreenPost: null,
    isGloballyMuted: true,
    showFullScreenVersion: false,
    isInitialising: true,
    embededPost: null,
    exitedPost: null,
  };

  const [globalState, setGlobalState] = useState(defaultState);
  const isConnectingRef = useRef(false);

  // ========================================
  // 1. BASE UTILS (Defined First)
  // ========================================
  const resetGlobalState = useCallback(() => setGlobalState(defaultState), []);
  const smallSetter = (key) => (value) => setGlobalState((p) => ({ ...p, [key]: value }));
  const showAnotherSignInModal = smallSetter("anotherSignInModal");
  const setLastUniqueMessagesCount = (val) =>
    setGlobalState(p => ({ ...p, lastUniqueMessagesCount: val }));

  const setNotificationCount = useCallback(
    (updater) =>
      setGlobalState((p) => ({
        ...p,
        notificationCount:
          typeof updater === "function" ? updater(p.notificationCount) : updater,
      })),
    []
  );

  const updateNotificationCount = useCallback(async (force = false) => {
    // Only fetch if authenticated or if forced
    const userId = globalState.user?.user_id;
    if (!userId && !force) return;

    try {
      const [nRes, mRes] = await Promise.all([
        fetch("/php/fetch_new_notifications_count.php", { credentials: "include" }),
        fetch("/php/unique_messages_count.php", { credentials: "include" }),
      ]);

      const n = await nRes.json();
      const m = await mRes.json();

      if (n.success && m.status === "success") {
        const unread = parseInt(n.unread_count) || 0;
        const msgCount = Object.values(m.uniqueMessagesCount || {}).reduce((a, b) => a + b, 0);
        setNotificationCount(unread + msgCount);
      }
    } catch (e) {
      console.error("Failed to fetch notification count", e);
    }
  }, [globalState.user]);

  // ========================================
  // 2. LOGOUT & NOTIFICATIONS
  // ========================================
  const internalLogout = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.close(1000, "User logged out");
    }
    socketRef.current = null;

    fetch("/php/logout.php", { method: "GET", credentials: "include" })
      .catch(console.error);

    resetGlobalState();
  }, [resetGlobalState]);

  const handleSocketError = (errorType) => {
    console.warn("WebSocket Issue:", errorType);
    if (errorType === "AUTH_FAILURE") {
      internalLogout(); // Only log out if the token is actually bad
    }
  }
  // ========================================
  // 3. WEBSOCKET ENGINE (Guarded)
  // ========================================
  const connectWebSocket = useCallback(() => {
    if (isConnectingRef.current) return;
    const { user_id, token } = globalState.user || {};
    if (!user_id || !token || !globalState.isAuthenticated) return;

    if (socketRef.current && (socketRef.current.readyState === 0 || socketRef.current.readyState === 1)) {
      return;
    }

    // CORRECT THE ORDER HERE:
    initializeWebSocket(
      user_id,                 // 1
      token,                   // 2
      socketRef,               // 3
      updateNotificationCount, // 4 (The socket calls this on open)
      internalLogout,          // 5 (Passed as logOut)
      showAnotherSignInModal,  // 6
      () => {                  // 7 (onClose)
        isConnectingRef.current = false;
      }
    );
  }, [globalState.user?.user_id, globalState.user?.token, globalState.isAuthenticated, updateNotificationCount, internalLogout, showAnotherSignInModal]);

  useEffect(() => {
    if (globalState.isAuthenticated) connectWebSocket();
  }, [globalState.isAuthenticated, connectWebSocket]);

  useEffect(() => {
    const handleReactivation = () => {
      const socketActive = socketRef.current && (socketRef.current.readyState === 0 || socketRef.current.readyState === 1);

      // Reconnect if authenticated and socket not active
      if (globalState.user?.user_id && !socketActive) {
        connectWebSocket();
      }

      // Always refresh notification count
      updateNotificationCount(true);
    };

    window.addEventListener("online", handleReactivation);
    document.addEventListener("visibilitychange", handleReactivation);

    return () => {
      window.removeEventListener("online", handleReactivation);
      document.removeEventListener("visibilitychange", handleReactivation);
    };
  }, [globalState.user, connectWebSocket, updateNotificationCount]);


  // Inside GlobalStateContext.js

  useEffect(() => {
    const verifySession = async () => {
      try {
        const response = await fetch("/php/check_auth.php", {
          method: "GET",
          credentials: "include",
        });

        const data = await response.json();

        if (data.authenticated) {
          // 1. Update state
          setGlobalState((prev) => ({
            ...prev,
            user: data,
            isAuthenticated: true,
            isInitialising: false,
          }));

          // 2. IMPORTANT: Call a version of the fetch that doesn't check globalState.isAuthenticated
          // because the state update above hasn't finished yet.
          performInitialCountFetch();
        } else {
          setGlobalState({ ...defaultState, isInitialising: false });
        }
      } catch (error) {
        setGlobalState({ ...defaultState, isInitialising: false });
      }
    };

    // Define a helper that doesn't rely on the 'isAuthenticated' state variable
    const performInitialCountFetch = async () => {
      try {
        const [nRes, mRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BASE_URL}/php/fetch_new_notifications_count.php`, { credentials: "include" }),
          fetch(`${process.env.REACT_APP_BASE_URL}/php/unique_messages_count.php`, { credentials: "include" })
        ]);
        const n = await nRes.json();
        const m = await mRes.json();
        if (n.success && m.status === "success") {
          const unread = parseInt(n.unread_count) || 0;
          const msgCount = Object.values(m.uniqueMessagesCount || {}).reduce((a, b) => a + b, 0);
          setNotificationCount(unread + msgCount);
        }
      } catch (e) {
        console.error("Initial count fetch failed", e);
      }
    };

    verifySession();
  }, []); // Empty array to run ONLY on mount

  const setUser = (up) => setGlobalState((p) => ({ ...p, user: typeof up === "function" ? up(p.user) : up }));
  const incrementPostCount = () => setGlobalState(p => ({ ...p, user: { ...p.user, noOfPosts: (p.user.noOfPosts || 0) + 1 } }));
  const setAuth = smallSetter("isAuthenticated");
  const setShowLogin = smallSetter("showLogin");
  const setShowLogins = smallSetter("showLogins");
  const setShowSignUp = smallSetter("showSignUp");
  const setShowVerify = smallSetter("showVerify");
  const setViewAsPublic = smallSetter("viewAsPublic");
  const setShowUserInfo = smallSetter("showUserInfo");
  const setGoTo = smallSetter("goTo");
  const setLikes = smallSetter("likes");
  const setShowNotifications = smallSetter("showNotifications");
  const setShowExplore = smallSetter("showExplore");
  const setHasPublicPosts = smallSetter("hasPublicPosts");
  const setMessageJD = smallSetter("messageJD");
  const setMoreInfoPost = smallSetter("moreInfoPost");
  const setSelectedConvo = smallSetter("selectedConvo");
  const setShowLink = smallSetter("showLink");
  const setJustPosted = smallSetter("justPosted");
  const setEmbededPost = smallSetter("embededPost");
  const setSessionExpired = smallSetter("sessionExpired");
  const setFullScreenPost = smallSetter("fullScreenPost");
  const setDuplicatePost = smallSetter("duplicatePost");
  const setIsGloballyMuted = smallSetter("isGloballyMuted");
  const setShowFullScreenVersion = smallSetter("showFullScreenVersion");
  const setExitedPost = smallSetter("exitedPost");
  const setPageName = (name) => setGlobalState((p) => ({ ...p, pageName: name }));
  const incrementRefreshKey = () => setGlobalState((p) => ({ ...p, refreshKey: p.refreshKey + 1 }));
  const resetRefreshKey = () => setGlobalState((p) => ({ ...p, refreshKey: -2 }));
  const setConversations = (updater) => setGlobalState((p) => ({ ...p, conversations: typeof updater === "function" ? updater(p.conversations) : updater }));

  const setRefreshKeyForPage = (page) =>
    setGlobalState((p) => ({
      ...p,
      refreshKeys: { ...p.refreshKeys, [page]: (p.refreshKeys[page] || 0) + 1 },
    }));

  const pauseAllVideos = (except = null) => {
    document.querySelectorAll("video").forEach((v) => { if (v !== except) v.pause(); });
  };

const removePostWithFade = (uuid) => {
  requestAnimationFrame(() => {
    document.querySelectorAll(`.post-${uuid}`).forEach((el) => {
      el.classList.add("hide");

      setTimeout(() => {
        el.remove(); // 🔥 REMOVE from DOM
      }, 500); // must match CSS transition
    });
  });
};


  return (
    <GlobalStateContext.Provider
      value={{
        globalState, setGlobalState, setUser, setLikes, setAuth, setConversations,
        setShowLogin, setShowLogins, setShowSignUp, setShowVerify, setViewAsPublic,
        setShowUserInfo, resetGlobalState, setGoTo, setShowExplore, setShowNotifications,
        addLike: (ids) => setGlobalState(p => ({ ...p, likes: [...new Set([...p.likes, ...(Array.isArray(ids) ? ids : [ids])])] })),
        removeLike: (id) => setGlobalState(p => ({ ...p, likes: p.likes.filter(i => i !== id) })),
        removePostWithFade,
        setHasPublicPosts, setMessageJD, setMoreInfoPost, updateNotificationCount,
        setNotificationCount, setRefreshKeyForPage, setPageName, incrementRefreshKey,
        resetRefreshKey, logOut: internalLogout, setSelectedConvo, setSessionExpired,
        showAnotherSignInModal, setShowLink, setJustPosted, setDuplicatePost,
        setFullScreenPost, setIsGloballyMuted, pauseAllVideos, setShowFullScreenVersion,
        socketRef, incrementPostCount, setEmbededPost, setExitedPost
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};

export const useGlobalState = () => useContext(GlobalStateContext);